import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { ticketsAPI, studentsAPI, teachersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORY_OPTIONS = [
  { value: 'teacher_academic', label: 'Teacher / academic' },
  { value: 'finance', label: 'Finance (kit, fees, refund)' },
  { value: 'sales_enrollment', label: 'Sales / enrollment' },
  { value: 'ops_other', label: 'Ops / other' },
  { value: 'batch_ops', label: 'Batch / scheduling' },
];

function personLabel(p) {
  if (!p) return '';
  const name =
    [p.first_name, p.last_name].filter(Boolean).join(' ').trim() ||
    p.name ||
    p.teacher_name ||
    p.email ||
    'Unknown';
  return name;
}

function unwrapList(res) {
  const body = res?.data;
  const raw = body?.data ?? body?.students ?? body?.teachers ?? body;
  return Array.isArray(raw) ? raw : [];
}

function teacherFromStudentDetail(detail) {
  const assignments = detail?.current_assignments || [];
  const withTeacher = assignments.filter((a) => a.teacher_id);
  if (!withTeacher.length) {
    return { teacher: null, multiple: false, batchName: null };
  }
  const uniqueIds = [...new Set(withTeacher.map((a) => String(a.teacher_id)))];
  const primary = withTeacher[0];
  const name = String(primary.teacher_name || '').trim();
  return {
    teacher: {
      id: primary.teacher_id,
      name: name || 'Teacher',
      teacher_name: name,
      first_name: name,
    },
    multiple: uniqueIds.length > 1,
    batchName: primary.batch_name || null,
  };
}

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx';

function isAllowedAttachment(file) {
  const name = String(file?.name || '');
  const type = String(file?.type || '').toLowerCase();
  if (name.toLowerCase().endsWith('.svg') || type === 'image/svg+xml') return false;
  if (/^image\/(png|jpe?g|webp|gif|heic|heif|bmp)$/.test(type)) return true;
  if (/\.(png|jpe?g|webp|gif|heic|heif|bmp|pdf|docx?|txt|xlsx?)$/i.test(name)) return true;
  return [
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ].includes(type);
}

function snapshotFile(file) {
  const type = file.type || 'image/png';
  const ext = type.includes('jpeg') ? 'jpg' : type.includes('webp') ? 'webp' : type.includes('gif') ? 'gif' : 'png';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return new File([file], `snapshot-${stamp}.${ext}`, { type });
}

function raiserTeacherFromUser(user) {
  if (!user?.teacherId) return null;
  const name =
    [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(' ').trim() ||
    user.fullName ||
    user.email ||
    'Teacher';
  return {
    id: user.teacherId,
    name,
    teacher_name: name,
    first_name: user.firstName || user.first_name || name,
    last_name: user.lastName || user.last_name || '',
  };
}

export default function TicketCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const raiserTeacher = useMemo(() => raiserTeacherFromUser(user), [user]);
  const lockTeacherToRaiser = user?.role === 'teacher' && Boolean(raiserTeacher?.id);
  const [category, setCategory] = useState('teacher_academic');
  const [priority, setPriority] = useState('medium');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [student, setStudent] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [studentInput, setStudentInput] = useState('');
  const [teacherInput, setTeacherInput] = useState('');
  const [debouncedStudent, setDebouncedStudent] = useState('');
  const [debouncedTeacher, setDebouncedTeacher] = useState('');
  const [teacherAutoNote, setTeacherAutoNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const autoFilledForStudentRef = useRef(null);
  const attachmentsRef = useRef([]);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  attachmentsRef.current = attachments;

  const needsStudent = category !== 'ops_other' && category !== 'batch_ops';
  const needsTeacher = category === 'teacher_academic';

  useEffect(() => {
    const t = setTimeout(() => setDebouncedStudent(studentInput.trim()), 250);
    return () => clearTimeout(t);
  }, [studentInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTeacher(teacherInput.trim()), 250);
    return () => clearTimeout(t);
  }, [teacherInput]);

  useEffect(() => {
    if (!needsTeacher) {
      setTeacher(null);
      setTeacherInput('');
      setTeacherAutoNote('');
      autoFilledForStudentRef.current = null;
      return;
    }
    if (lockTeacherToRaiser && raiserTeacher) {
      setTeacher(raiserTeacher);
      setTeacherInput(personLabel(raiserTeacher));
      setTeacherAutoNote(
        'Linked to you — the teacher raising this ticket. Change only if the issue is about another teacher.'
      );
    }
  }, [needsTeacher, lockTeacherToRaiser, raiserTeacher]);

  const studentsQuery = useQuery(
    ['ticket-students', debouncedStudent],
    async () => {
      const res = await studentsAPI.getAll({
        search: debouncedStudent || undefined,
        limit: 30,
        include_rm: true,
      });
      return unwrapList(res);
    },
    { keepPreviousData: true }
  );

  const teachersQuery = useQuery(
    ['ticket-teachers', debouncedTeacher],
    async () => {
      const res = await teachersAPI.getAll({
        search: debouncedTeacher || undefined,
        limit: 30,
      });
      return unwrapList(res);
    },
    { enabled: needsTeacher, keepPreviousData: true }
  );

  const linkedTeacherQuery = useQuery(
    ['ticket-student-linked-teacher', student?.id],
    async () => {
      const res = await studentsAPI.getById(student.id);
      return teacherFromStudentDetail(res.data.data);
    },
    { enabled: needsTeacher && Boolean(student?.id) && !lockTeacherToRaiser }
  );

  useEffect(() => {
    if (!needsTeacher || lockTeacherToRaiser) return;
    if (!student?.id) {
      autoFilledForStudentRef.current = null;
      setTeacher(null);
      setTeacherInput('');
      setTeacherAutoNote('');
      return;
    }
    if (!linkedTeacherQuery.data || linkedTeacherQuery.isFetching) return;
    if (autoFilledForStudentRef.current === String(student.id)) return;

    autoFilledForStudentRef.current = String(student.id);
    const { teacher: linked, multiple, batchName } = linkedTeacherQuery.data;
    if (linked?.id) {
      setTeacher(linked);
      setTeacherInput(personLabel(linked));
      setTeacherAutoNote(
        multiple
          ? `Teacher auto-selected from active batch${batchName ? ` “${batchName}”` : ''}. Student has multiple teachers — change if needed.`
          : `Teacher auto-selected from active batch${batchName ? ` “${batchName}”` : ''}.`
      );
    } else {
      setTeacher(null);
      setTeacherInput('');
      setTeacherAutoNote('No active batch teacher found for this student. Select a teacher manually.');
    }
  }, [needsTeacher, lockTeacherToRaiser, student?.id, linkedTeacherQuery.data, linkedTeacherQuery.isFetching]);

  const students = useMemo(() => {
    const list = studentsQuery.data || [];
    if (student && !list.some((s) => String(s.id) === String(student.id))) {
      return [student, ...list];
    }
    return list;
  }, [studentsQuery.data, student]);

  const teachers = useMemo(() => {
    const list = teachersQuery.data || [];
    if (teacher && !list.some((t) => String(t.id) === String(teacher.id))) {
      return [teacher, ...list];
    }
    return list;
  }, [teachersQuery.data, teacher]);

  const previewReady =
    Boolean(category) && (!needsTeacher || Boolean(teacher?.id));

  const previewQuery = useQuery(
    ['ticket-assignment-preview', category, teacher?.id || null],
    async () => {
      const res = await ticketsAPI.previewAssignment({
        category,
        teacher_id: teacher?.id || undefined,
      });
      return res.data.data;
    },
    { enabled: previewReady, keepPreviousData: true }
  );

  const createMut = useMutation((body) => ticketsAPI.create(body), {
    onSuccess: (res) => {
      const ticket = res.data.data;
      const who =
        ticket?.assignee_name?.trim() ||
        (ticket?.queue_role ? `${ticket.queue_role} queue` : '');
      toast.success(
        who
          ? `Created ${ticket.ticket_number} — assigned to ${who}`
          : `Created ${ticket?.ticket_number || 'ticket'}`
      );
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create ticket'),
  });

  const addAttachments = useCallback((list, { fromClipboard = false } = {}) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const next = [...attachmentsRef.current];
    const before = next.length;
    for (const raw of incoming) {
      const file = fromClipboard ? snapshotFile(raw) : raw;
      if (next.length >= MAX_ATTACHMENTS) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`);
        break;
      }
      if (!file || file.size <= 0) {
        toast.error('That snapshot was empty');
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name || 'File'} is larger than 10 MB`);
        continue;
      }
      if (!isAllowedAttachment(file)) {
        toast.error('Use an image, PDF, Word, Excel, or text file');
        continue;
      }
      const previewUrl = String(file.type || '').startsWith('image/') ? URL.createObjectURL(file) : '';
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl,
      });
    }
    attachmentsRef.current = next;
    setAttachments(next);
    const added = next.length - before;
    if (fromClipboard && added > 0) {
      toast.success(added === 1 ? 'Snapshot added' : `${added} snapshots added`);
    }
  }, []);

  const removeAttachment = (id) => {
    const next = attachmentsRef.current.filter((item) => {
      if (item.id === id && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return item.id !== id;
    });
    attachmentsRef.current = next;
    setAttachments(next);
  };

  useEffect(() => {
    const onPaste = (event) => {
      const form = formRef.current;
      const active = document.activeElement;
      const insideForm = Boolean(form && active && form.contains(active));
      const pageFocused = !active || active === document.body || active === document.documentElement;
      if (!insideForm && !pageFocused) return;
      const items = Array.from(event.clipboardData?.items || []);
      let images = items
        .filter((item) => item.kind === 'file' && /^image\/(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(item.type || ''))
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (!images.length) {
        images = Array.from(event.clipboardData?.files || []).filter((file) =>
          /^image\/(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(file.type || '')
        );
      }
      if (!images.length) return;
      event.preventDefault();
      addAttachments(images, { fromClipboard: true });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addAttachments]);

  useEffect(
    () => () => {
      attachmentsRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    },
    []
  );

  const onSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error('Subject and description are required');
      return;
    }
    if (needsStudent && !student?.id) {
      toast.error('Select a student');
      return;
    }
    if (needsTeacher && !teacher?.id) {
      toast.error('Select a teacher');
      return;
    }
    const fields = {
      category,
      priority,
      subject: subject.trim(),
      description: description.trim(),
      student_id: student?.id || '',
      teacher_id: needsTeacher ? teacher.id : '',
    };
    if (!attachments.length) {
      createMut.mutate({
        ...fields,
        student_id: student?.id || null,
        teacher_id: needsTeacher ? teacher.id : null,
      });
      return;
    }
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value) form.append(key, value);
    });
    attachments.forEach((item) => form.append('files', item.file, item.file.name));
    createMut.mutate(form);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" mb={2}>
        New ticket
      </Typography>
      <Paper sx={{ p: 2 }} component="form" onSubmit={onSubmit} ref={formRef}>
        <Stack spacing={2}>
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {CATEGORY_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            fullWidth
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            required
            multiline
            minRows={4}
          />

          <Box>
            <Typography variant="subtitle2" mb={0.5}>
              Attachment
            </Typography>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addAttachments(e.dataTransfer?.files);
              }}
              sx={{
                border: '1px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                bgcolor: dragOver ? 'action.hover' : 'background.default',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" mb={1}>
                Upload a file, drop it here, or paste a snapshot with Ctrl+V. Images, PDF, Word, Excel, or text.
                Up to {MAX_ATTACHMENTS} files, 10 MB each.
              </Typography>
              <Button type="button" variant="outlined" onClick={() => fileInputRef.current?.click()}>
                Upload file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={(e) => {
                  addAttachments(e.target.files);
                  e.target.value = '';
                }}
              />
            </Box>
            {attachments.length ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mt={1}>
                {attachments.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1, width: 160 }}>
                    {item.previewUrl ? (
                      <Box
                        component="img"
                        src={item.previewUrl}
                        alt={item.file.name}
                        sx={{ width: '100%', height: 96, objectFit: 'cover', borderRadius: 0.5, display: 'block' }}
                      />
                    ) : (
                      <Typography variant="body2" noWrap title={item.file.name}>
                        {item.file.name}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {item.file.name}
                    </Typography>
                    <Button type="button" size="small" onClick={() => removeAttachment(item.id)}>
                      Remove
                    </Button>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Box>

          {(needsStudent || category === 'ops_other' || category === 'batch_ops') && (
            <Autocomplete
              options={students}
              value={student}
              onChange={(_e, value) => {
                setStudent(value);
                if (needsTeacher) autoFilledForStudentRef.current = null;
              }}
              inputValue={studentInput}
              onInputChange={(_e, value) => setStudentInput(value)}
              getOptionLabel={(o) => personLabel(o)}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              loading={studentsQuery.isFetching}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={needsStudent ? 'Student *' : 'Student (optional)'}
                  placeholder="Type to search students"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {studentsQuery.isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {needsTeacher ? (
            <>
              <Autocomplete
                options={teachers}
                value={teacher}
                onChange={(_e, value) => {
                  setTeacher(value);
                  setTeacherAutoNote(value ? 'Teacher selected manually.' : '');
                }}
                inputValue={teacherInput}
                onInputChange={(_e, value) => setTeacherInput(value)}
                getOptionLabel={(o) => personLabel(o)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                loading={teachersQuery.isFetching || linkedTeacherQuery.isFetching}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Teacher *"
                    placeholder="Auto-filled from student, or type to search"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {teachersQuery.isFetching || linkedTeacherQuery.isFetching ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {teacherAutoNote ? (
                <Alert severity={teacher?.id ? 'success' : 'warning'}>{teacherAutoNote}</Alert>
              ) : student?.id && linkedTeacherQuery.isFetching ? (
                <Alert severity="info">Looking up this student’s teacher…</Alert>
              ) : null}
            </>
          ) : null}

          {previewReady ? (
            <Alert severity="info" icon={previewQuery.isFetching ? <CircularProgress size={18} /> : undefined}>
              {previewQuery.data?.summary ||
                (needsTeacher && !teacher
                  ? 'Select a teacher to see who will receive this ticket.'
                  : 'Resolving assignee…')}
              {previewQuery.data?.mode === 'queue' && previewQuery.data?.queueMembers?.length > 1 ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {previewQuery.data.queueMembers.map((m) => m.name).join(' · ')}
                </Typography>
              ) : null}
            </Alert>
          ) : needsTeacher ? (
            <Alert severity="warning">Select a teacher to see who this ticket will be assigned to.</Alert>
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => navigate('/tickets')}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={createMut.isLoading}>
              Create ticket
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
